import { tonicToPitchClass } from './pitchLabel';
import type { EarTrainingMode } from '../types/earTraining';

/** Major scale semitones from tonic; minor (la-based natural) from tonic. */
const MAJOR_STEPS = [0, 2, 4, 5, 7, 9, 11] as const;
const MINOR_STEPS = [0, 2, 3, 5, 7, 8, 10] as const;

export const MAJOR_SYLLABLES = ['do', 're', 'mi', 'fa', 'sol', 'la', 'ti'] as const;
/** La-based minor: scale degree 1 = la */
export const MINOR_SYLLABLES = ['la', 'ti', 'do', 're', 'mi', 'fa', 'sol'] as const;

/** Chromatic alterations (raised degree syllables in major / la-minor context). */
const RAISED: Record<number, string> = {
  1: 'di',
  3: 'fi',
  4: 'si',
  6: 'li',
};
const LOWERED: Record<number, string> = {
  1: 'ra',
  3: 'me',
  4: 'se',
  6: 'le',
  7: 'te',
};

function findScaleDegree(pc: number, tonicPc: number, steps: readonly number[]): number | null {
  const interval = ((pc - tonicPc) % 12 + 12) % 12;
  const idx = steps.indexOf(interval as (typeof steps)[number]);
  return idx >= 0 ? idx + 1 : null;
}

function nearestDegreeWithAlter(
  pc: number,
  tonicPc: number,
  steps: readonly number[],
  syllables: readonly string[]
): string {
  const interval = ((pc - tonicPc) % 12 + 12) % 12;
  const degree = findScaleDegree(pc, tonicPc, steps);
  if (degree !== null) return syllables[degree - 1]!;

  for (let d = 1; d <= 7; d++) {
    const step = steps[d - 1]!;
    if ((step + 1) % 12 === interval) {
      const raised = RAISED[d];
      if (raised) return raised;
    }
    if ((step + 11) % 12 === interval) {
      const lowered = LOWERED[d];
      if (lowered) return lowered;
    }
  }

  return '?';
}

/** Semitone step from tonic (la in minor) for a diatonic syllable; null if not a scale degree. */
export function semitoneStepForSyllable(syllable: string, mode: EarTrainingMode): number | null {
  if (mode === 'major') {
    const idx = MAJOR_SYLLABLES.indexOf(syllable as (typeof MAJOR_SYLLABLES)[number]);
    return idx >= 0 ? MAJOR_STEPS[idx]! : null;
  }
  if (syllable === 'si') return 11;
  const idx = MINOR_SYLLABLES.indexOf(syllable as (typeof MINOR_SYLLABLES)[number]);
  return idx >= 0 ? MINOR_STEPS[idx]! : null;
}

/**
 * Movable do: major → do = tonic; minor → la = tonic (key signature = relative major).
 * Harmonic minor raised 7th → si when that pitch appears.
 */
export function midiToSolfege(
  midi: number,
  tonic: string,
  mode: EarTrainingMode,
  _keySignatureFifths: number
): string {
  const pc = ((midi % 12) + 12) % 12;
  const tonicPc = tonicToPitchClass(tonic);

  if (mode === 'major') {
    return nearestDegreeWithAlter(pc, tonicPc, MAJOR_STEPS, MAJOR_SYLLABLES);
  }

  const leadingPc = (tonicPc + 11) % 12;
  if (pc === leadingPc) return 'si';

  const natural = nearestDegreeWithAlter(pc, tonicPc, MINOR_STEPS, MINOR_SYLLABLES);
  if (natural !== '?') return natural;

  return '?';
}

/** Short key label for hints (no signature detail). */
export function formatKeyLabelShort(tonic: string, mode: EarTrainingMode): string {
  return mode === 'major' ? `${tonic} major` : `${tonic} minor`;
}

export function formatKeyLabel(tonic: string, mode: EarTrainingMode, fifths: number): string {
  const sig =
    fifths === 0
      ? 'no sharps or flats'
      : fifths > 0
        ? `${fifths} sharp${fifths > 1 ? 's' : ''}`
        : `${-fifths} flat${-fifths > 1 ? 's' : ''}`;
  return mode === 'major' ? `${tonic} major (${sig})` : `${tonic} minor (${sig})`;
}
